import React, { useState, useEffect } from 'react'
import {
    Drawer,
    makeStyles,
    Typography,
    IconButton,
    Divider,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { TOP_BAR_HEIGHT } from '../../../navigation/constants'
import { BillableOrgs } from '../../../hooks/useBilling'
import { BillingGroup } from '../../../models/Billing/types'
import { useBillingGroups } from '../../../hooks/useBillingGroups'
import { AxiosResponse } from 'axios'

export interface Company {
    id: number
    name: string
}

export interface InvoiceFilters {
    invoiceNumber?: string
    status?: string[]
    draftSubStatus?: string
    organization?: number
    startDate?: string
    endDate?: string
    company?: number
    balance?: number
    balanceOperator?: 'eq' | 'gt' | 'lt' | 'gte' | 'lte'
    billingGroup?: number
}

const DRAWER_WIDTH = 320

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: DRAWER_WIDTH,
        flexShrink: 0,
    },
    drawerPaper: {
        width: DRAWER_WIDTH,
        padding: theme.spacing(2),
        top: TOP_BAR_HEIGHT,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    filterGroup: {
        flex: 1,
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
    },
    filterItem: {
        marginBottom: theme.spacing(2),
    },
    actions: {
        display: 'flex',
        flex: 1,
        maxHeight: 50,
        marginBottom: theme.spacing(6),
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    selectField: {
        marginBottom: theme.spacing(2),
    },
    typeFilterItem: {
        marginBottom: theme.spacing(2),
    },
}))

interface Props {
    open: boolean
    onClose: () => void
    filters: InvoiceFilters
    onFiltersChange: (filters: InvoiceFilters) => void
    onApply: (filters?: InvoiceFilters) => void
    onReset: () => void
    isLoading?: boolean
    companies?: Company[]
    organizations?: BillableOrgs[]
    showCompanyFilter?: boolean
    billingGroups?: BillingGroup[] | null
}

export const FilterDrawer = ({
    open,
    onClose,
    filters,
    onFiltersChange,
    onApply,
    onReset,
    isLoading = false,
    organizations = [],
    billingGroups,
}: Props) => {
    const classes = useStyles()
    const [localFilters, setLocalFilters] = useState<InvoiceFilters>(filters)
    const [billingGroupSearch, setBillingGroupSearch] = useState('')
    const [filteredBillingGroups, setFilteredBillingGroups] = useState<
        BillingGroup[]
    >([])
    const [billingGroupSelected, setBillingGroupSelected] = useState(false)
    const [draftSubStatusError, setDraftSubStatusError] = useState('')
    const theme = useTheme()

    // Update local filters when props change
    useEffect(() => {
        setLocalFilters(filters)
    }, [filters])

    useEffect(() => {
        if (!billingGroups) {
            setFilteredBillingGroups([])
            return
        }
        setFilteredBillingGroups(
            billingGroups.filter((bg) => {
                const displayName = bg.quickbooks_customer.display_name
                const search = billingGroupSearch.toLowerCase()
                return displayName.toLowerCase().includes(search)
            }),
        )
    }, [billingGroupSearch, billingGroups])

    const handleChange = (field: keyof InvoiceFilters) => (
        event: React.ChangeEvent<HTMLInputElement | { value: unknown }>,
    ) => {
        const newFilters = {
            ...localFilters,
            [field]: event.target.value,
        }
        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    const handleStatusChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const value = event.target.value as string[]
        const newFilters = { ...localFilters, status: value }

        // If a draft sub status is selected, only allow "Draft" as status
        if (
            localFilters.draftSubStatus &&
            (!value.includes('DRAFT') || value.length > 1)
        ) {
            setDraftSubStatusError(
                'Cannot select a draft sub status with other statuses.',
            )
            // Only allow "Draft"
            newFilters.status = ['DRAFT']
        } else {
            setDraftSubStatusError('')
            // If "Draft" is not selected, clear draft sub status
            if (!value.includes('DRAFT')) {
                newFilters.draftSubStatus = ''
            }
        }

        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    const handleDraftSubStatusChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const value = event.target.value as string
        const newFilters = { ...localFilters, draftSubStatus: value }

        // If any status other than "Draft" is selected, show error
        if (
            localFilters.status &&
            (!localFilters.status.includes('DRAFT') ||
                localFilters.status.length > 1)
        ) {
            setDraftSubStatusError(
                'Cannot select a draft sub status with other statuses.',
            )
            return
        } else {
            setDraftSubStatusError('')
            // Force status to "Draft" if not already
            if (
                !localFilters.status ||
                !localFilters.status.includes('DRAFT')
            ) {
                newFilters.status = ['DRAFT']
            }
        }

        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    const handleOrganizationChange: React.ChangeEventHandler<{
        value: unknown
    }> = (event) => {
        const value = event.target.value as number
        const newFilters = {
            ...localFilters,
            organization: value,
        }
        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    const [balanceFilter, setBalanceFilter] = useState<{
        value: number
        operator: 'eq' | 'gt' | 'lt' | 'gte' | 'lte'
    }>({
        value: filters.balance || 0,
        operator: filters.balanceOperator || 'eq',
    })

    const handleReset = () => {
        const emptyFilters: InvoiceFilters = {}
        setLocalFilters(emptyFilters)
        onFiltersChange(emptyFilters)
        setBillingGroupSearch('')
        setBillingGroupSelected(false)
        onReset()
    }

    const handleDeleteFilter = (filter: keyof InvoiceFilters) => () => {
        const newFilters = {
            ...localFilters,
            [filter]: undefined,
        }
        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    const handleYearToDate = () => {
        const currentYear = new Date().getFullYear()
        setLocalFilters({
            ...localFilters,
            startDate: `${currentYear}-01-01`,
        })
    }

    const handleTrailing12Months = () => {
        const currentDate = new Date()
        const startDate = new Date(currentDate)
        startDate.setFullYear(startDate.getFullYear() - 1)
        setLocalFilters({
            ...localFilters,
            startDate: startDate.toISOString().split('T')[0],
        })
    }

    // Billing Cycles are monthly so we just need to find the first
    // day of the current month and set the start date to that
    const handleLastBillingCycle = () => {
        const currentDate = new Date()
        const startDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            1,
        )
        setLocalFilters({
            ...localFilters,
            startDate: startDate.toISOString().split('T')[0],
        })
    }

    return (
        <Drawer
            className={classes.drawer}
            variant="persistent"
            anchor="right"
            open={open}
            classes={{
                paper: classes.drawerPaper,
            }}
        >
            <div className={classes.header}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            <Divider />

            <div className={classes.filterGroup}>
                <TextField
                    fullWidth
                    label="Invoice Number"
                    value={localFilters.invoiceNumber || ''}
                    onChange={handleChange('invoiceNumber')}
                    className={classes.filterItem}
                    placeholder="EZOS-123"
                />

                <FormControl fullWidth className={classes.filterItem}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        multiple
                        value={localFilters.status || []}
                        onChange={handleStatusChange}
                        renderValue={(selected) =>
                            (selected as string[]).join(', ')
                        }
                        disabled={!!localFilters.draftSubStatus}
                    >
                        <MenuItem value="DRAFT">Draft</MenuItem>
                        <MenuItem value="SENT">Sent</MenuItem>
                        <MenuItem value="PAID">Paid</MenuItem>
                        <MenuItem value="OVERDUE">Overdue</MenuItem>
                        <MenuItem value="VOIDED">Voided</MenuItem>
                    </Select>
                </FormControl>

                <FormControl
                    fullWidth
                    className={classes.filterItem}
                    error={!!draftSubStatusError}
                >
                    <InputLabel>Draft Status</InputLabel>
                    <Select
                        value={localFilters.draftSubStatus || ''}
                        onChange={handleDraftSubStatusChange}
                        renderValue={(selected) => selected as string}
                        disabled={
                            localFilters.status &&
                            (!localFilters.status.includes('DRAFT') ||
                                localFilters.status.length > 1)
                        }
                    >
                        <MenuItem value="NEEDS_REVIEW">Needs Review</MenuItem>
                        <MenuItem value="READY_TO_SEND">Ready to Send</MenuItem>
                    </Select>
                    {draftSubStatusError && (
                        <Typography color="error" variant="caption">
                            {draftSubStatusError}
                        </Typography>
                    )}
                </FormControl>

                <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={localFilters.startDate || ''}
                    onChange={handleChange('startDate')}
                    className={classes.filterItem}
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={localFilters.endDate || ''}
                    onChange={handleChange('endDate')}
                    className={classes.filterItem}
                    InputLabelProps={{ shrink: true }}
                />

                <div
                    className={classes.filterItem}
                    style={{ display: 'flex', gap: theme.spacing(1) }}
                >
                    <Tooltip title="Year to Date">
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.filterItem}
                            onClick={handleYearToDate}
                        >
                            YTD
                        </Button>
                    </Tooltip>

                    <Tooltip title="Trailing 12 Months">
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.filterItem}
                            onClick={handleTrailing12Months}
                        >
                            TTM
                        </Button>
                    </Tooltip>

                    <Tooltip title="Last Billing Cycle">
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.filterItem}
                            onClick={handleLastBillingCycle}
                        >
                            Last Cycle
                        </Button>
                    </Tooltip>
                </div>

                <FormControl fullWidth className={classes.filterItem}>
                    <InputLabel>Organization</InputLabel>
                    <Select
                        value={localFilters.organization || ''}
                        onChange={handleOrganizationChange}
                    >
                        {organizations.map((organization) => (
                            <MenuItem
                                key={organization.id}
                                value={organization.id}
                            >
                                {organization.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Search Billing Group"
                    value={billingGroupSearch}
                    onChange={(e) => {
                        const value = e.target.value
                        setBillingGroupSearch(value)
                        setBillingGroupSelected(false)

                        // Clear billing group filter when search is empty
                        if (value === '') {
                            const newFilters = {
                                ...localFilters,
                                billingGroup: undefined,
                            }
                            setLocalFilters(newFilters)
                            onFiltersChange(newFilters)
                        }
                    }}
                    className={classes.filterItem}
                />
                {billingGroupSearch && !billingGroupSelected && (
                    <div>
                        {filteredBillingGroups.map((bg) => (
                            <MenuItem
                                key={bg.id}
                                onClick={() => {
                                    const newFilters = {
                                        ...localFilters,
                                        billingGroup: bg.id,
                                    }
                                    setLocalFilters(newFilters)
                                    onFiltersChange(newFilters)
                                    setBillingGroupSearch(
                                        bg.quickbooks_customer.display_name,
                                    )
                                    setBillingGroupSelected(true)
                                }}
                            >
                                {bg.quickbooks_customer.display_name}
                            </MenuItem>
                        ))}
                    </div>
                )}
            </div>

            <div className={classes.actions}>
                <Button onClick={handleReset}>Reset</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onApply(localFilters)}
                    disabled={isLoading}
                >
                    Apply
                </Button>
            </div>
        </Drawer>
    )
}
